import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {RequiredMark} from 'src/components/required-mark';
import {NOTIFICATION_TIMEOUTS} from 'src/constants';
import {useApiWithNotifications} from 'src/hooks/api-with-notification/useApiWithNotifications';
import {
    ALLOWED_VENDOR_NAME_REGEX,
    MAX_VENDOR_DESCRIPTION_LENGTH,
    MAX_VENDOR_NAME_LENGTH,
    MIN_VENDOR_NAME_LENGTH,
    normalizeVendorName,
    sanitizeVendorName,
} from 'src/modules/vendor/shared/vendorValidation';
import {createVendor} from 'src/openapi/admin/apis/VendorManagementApi';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {Textarea} from '@pb/kit-component-textarea-v2';
import Typography from '@pb/kit-component-typography';


import type {Vendor} from 'src/openapi/admin/models/Vendor';

type Props = {
    shown: boolean,
    onClose: () => void,
    existingVendors: Vendor[],
    onVendorCreated: (vendor: Vendor) => void,
};

const DUPLICATE_NAME_MESSAGE = 'A vendor with this name already exists';
const GENERIC_ERROR_MESSAGE = 'Unable to create vendor. Please try again';

export const AddVendorDialog: React.FC<Props> = ({
    shown,
    onClose,
    existingVendors,
    onVendorCreated,
}) => {
    const [vendorName, setVendorName] = useState('');
    const [description, setDescription] = useState('');
    const [nameError, setNameError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const {executeWithNotifications} = useApiWithNotifications();

    const normalizedVendorName = normalizeVendorName(vendorName);

    const hasDuplicateName = useMemo(() => {
        if (!normalizedVendorName) {
            return false;
        }
        return existingVendors.some(
            vendor => vendor.vendorName.trim().toLowerCase() === normalizedVendorName,
        );
    }, [existingVendors, normalizedVendorName]);

    const resetForm = useCallback(() => {
        setVendorName('');
        setDescription('');
        setNameError(null);
        setBusy(false);
    }, []);

    useEffect(() => {
        if (!shown) {
            resetForm();
        }
    }, [shown, resetForm]);

    useEffect(() => {
        if (nameError === DUPLICATE_NAME_MESSAGE && !hasDuplicateName) {
            setNameError(null);
        }
    }, [hasDuplicateName, nameError]);

    const validateVendorName = useCallback(() => {
        const hasMinimumLength = normalizedVendorName.length >= MIN_VENDOR_NAME_LENGTH;
        const isAllowedName = ALLOWED_VENDOR_NAME_REGEX.test(vendorName.trim());
        if (hasMinimumLength && !isAllowedName) {
            return false;
        }
        if (hasDuplicateName) {
            setNameError(DUPLICATE_NAME_MESSAGE);
            return false;
        }
        setNameError(null);
        return true;
    }, [hasDuplicateName, normalizedVendorName, vendorName]);

    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose, resetForm]);

    const handleAddVendor = useCallback(async () => {
        if (vendorName.trim().length < MIN_VENDOR_NAME_LENGTH) {
            return;
        }
        const isValid = validateVendorName();
        if (!isValid) {
            return;
        }

        setBusy(true);

        try {
            const payload = {
                vendorName: vendorName.trim(),
                description: description.trim() || undefined,
            };

            const response = await executeWithNotifications<Vendor>(
                createVendor({createVendorRequest: payload}),
                {
                    success: 'Vendor successfully created.',
                    error: GENERIC_ERROR_MESSAGE,
                    exception: GENERIC_ERROR_MESSAGE,
                },
                'create-vendor',
                NOTIFICATION_TIMEOUTS,
            );

            if (response.success && response.data) {
                onVendorCreated(response.data);
                handleClose();

            }
        } finally {
            setBusy(false);
        }
    }, [description, executeWithNotifications, handleClose, onVendorCreated, validateVendorName, vendorName]);

    const handleVendorNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        const sanitizedValue = sanitizeVendorName(value);

        setVendorName(sanitizedValue);

        if (value !== sanitizedValue) {
            return;
        }

        if (nameError && nameError !== DUPLICATE_NAME_MESSAGE) {
            setNameError(null);
        }
    }, [nameError]);

    const handleDescriptionChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(event.target.value);
    }, []);

    const handleDialogShownChange = useCallback((visible: boolean) => {
        if (!visible) {
            handleClose();
        }
    }, [handleClose]);

    const isAddDisabled = busy
        || normalizedVendorName.length < MIN_VENDOR_NAME_LENGTH
        || hasDuplicateName
        || !!nameError;
    const displayError = nameError === DUPLICATE_NAME_MESSAGE ? DUPLICATE_NAME_MESSAGE : null;

    return (
        <Dialog
            shown={shown}
            setShown={handleDialogShownChange}>
            <DialogHeader onClose={handleClose}>
                <Typography variant="heading-level-2">
                    Add Vendor
                </Typography>
            </DialogHeader>
            <Group direction="v" gap="m" style={{padding: 24, width: 400}}>
                {displayError ? (
                    <Message color="error" style={{paddingBottom: 4}}>
                        {displayError}
                    </Message>
                ) : null}
                <Item>
                    <Label htmlFor="vendor-name-input"><RequiredMark label="Vendor Name"/></Label>
                    <Input
                        id="vendor-name-input"
                        value={vendorName}
                        onChange={handleVendorNameChange}
                        onBlur={validateVendorName}
                        placeholder="Enter vendor name (min. 3 characters)"
                        autoComplete="off"
                        autoCorrect="off"
                        maxLength={MAX_VENDOR_NAME_LENGTH}
                        required={true}
                    />
                </Item>
                <Item>
                    <Label htmlFor="vendor-description-input">Description</Label>
                    <Textarea
                        id="vendor-description-input"
                        value={description}
                        onChange={handleDescriptionChange}
                        placeholder="Describe the vendor or their data access context"
                        resizable={false}
                        maxLength={MAX_VENDOR_DESCRIPTION_LENGTH}
                    />
                </Item>
            </Group>
            <DialogFooter>
                <Button
                    variant="Type.PRIMARY"
                    disabled={isAddDisabled}
                    busy={busy}
                    onClick={handleAddVendor}>
                    Add
                </Button>
                <Button onClick={handleClose} busy={busy} variant="Type.SECONDARY">
                    Cancel
                </Button>
            </DialogFooter>
        </Dialog>
    );
};
